import { Plus, Trash2 } from 'lucide-react';
import type { SlideLeafBlock } from '@hcc/types';
import { Button } from '../../../ui/Button';
import { SHARED_INPUT_CLASS } from '../pageMeta';

type TableBlock = Extract<SlideLeafBlock, { type: 'table' }>;

/** Header + cell grid editor; rows always track the header width. */
export function TableBlockEditor({
  block,
  onChange,
}: {
  block: TableBlock;
  onChange: (next: TableBlock) => void;
}) {
  function setHeader(i: number, value: string) {
    onChange({ ...block, header: block.header.map((h, j) => (j === i ? value : h)) });
  }
  function setCell(r: number, c: number, value: string) {
    onChange({
      ...block,
      rows: block.rows.map((row, ri) =>
        ri === r ? row.map((cell, ci) => (ci === c ? value : cell)) : row,
      ),
    });
  }
  function addColumn() {
    onChange({
      ...block,
      header: [...block.header, `Column ${block.header.length + 1}`],
      rows: block.rows.map((row) => [...row, '']),
    });
  }
  function removeColumn(i: number) {
    onChange({
      ...block,
      header: block.header.filter((_, j) => j !== i),
      rows: block.rows.map((row) => row.filter((_, j) => j !== i)),
    });
  }
  function addRow() {
    onChange({ ...block, rows: [...block.rows, block.header.map(() => '')] });
  }
  function removeRow(i: number) {
    onChange({ ...block, rows: block.rows.filter((_, j) => j !== i) });
  }

  const cellClass = `${SHARED_INPUT_CLASS} mt-0 min-w-24`;
  return (
    <div className="space-y-2 overflow-x-auto">
      <div className="flex items-center gap-2">
        {block.header.map((h, i) => (
          <div key={i} className="flex flex-1 items-center gap-1">
            <input
              value={h}
              onChange={(e) => setHeader(i, e.target.value)}
              placeholder={`Column ${i + 1}`}
              className={`${cellClass} font-medium`}
            />
            <button
              type="button"
              aria-label={`Remove column ${i + 1}`}
              disabled={block.header.length <= 1}
              onClick={() => removeColumn(i)}
              className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button variant="ghost" size="sm" iconLeft={<Plus className="h-3.5 w-3.5" />} onClick={addColumn}>
          Column
        </Button>
      </div>
      {block.rows.map((row, r) => (
        <div key={r} className="flex items-center gap-2">
          {row.map((cell, c) => (
            <input
              key={c}
              value={cell}
              onChange={(e) => setCell(r, c, e.target.value)}
              aria-label={`Row ${r + 1}, ${block.header[c] || `column ${c + 1}`}`}
              className={`${cellClass} flex-1`}
            />
          ))}
          <button
            type="button"
            aria-label={`Remove row ${r + 1}`}
            disabled={block.rows.length <= 1}
            onClick={() => removeRow(r)}
            className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" iconLeft={<Plus className="h-3.5 w-3.5" />} onClick={addRow}>
        Row
      </Button>
    </div>
  );
}
